<template>
  <div style="display: contents;">
    <confirm
      v-if="allowDelete"
      id="delete-file-confirm"
      ref="deleteFileConfirm"
      @confirm="confirmDelete"
    >
      <template v-slot:title>{{ t('filesGallery-delete_confirm-title') }}</template>
      <template v-slot:body>
        <p v-html="t('filesGallery-delete_confirm-question', [fileToDelete ? fileToDelete.name : ''])"></p>
        <p>{{ t('filesGallery-delete_confirm-warning') }}</p>
        <p class="text-warning">{{ t('filesGallery-delete_confirm-irreversible') }}</p>
      </template>
    </confirm>

    <KvModal
      ref="modal"
      :id="id"
      :size="size"
      class="kv-fgm-modal"
      :modelValue="modelValue"
      @update:modelValue="onUpdateModelValue"
      @open="onOpen"
      @close="handleClose"
    >
      <template #title>
        <slot name="title">{{ t('filesGallery-modal-title') }}</slot>
      </template>
      <template #subtitle>
        <slot name="subtitle">{{ t('filesGallery-modal-subtitle') }}</slot>
      </template>

      <template #body>
        <div class="kv-files-gallery-modal">
          <div class="kv-fgm__layout">
            <section class="kv-fgm__picker">
              <input
                v-if="allowUpload"
                ref="fileInput"
                type="file"
                multiple
                hidden
                @change="handleFileUpload"
              />

              <div class="kv-fgm__toolbar">
                <div class="kv-fgm__search">
                  <input
                    v-model="search"
                    :placeholder="t('filesGallery-search_placeholder')"
                    :aria-label="t('filesGallery-search_placeholder')"
                    type="text"
                    class="form-control"
                  />
                  <KvButton
                    v-if="search && search.trim()"
                    class="kv-fgm__clear"
                    variant="text"
                    size="small"
                    @click="search = ''"
                    :title="t('filesGallery-modal-clear')"
                  >
                    <Icon icon-class="ui-x-close" size="4" />
                  </KvButton>
                </div>

                <KvButton
                  v-if="allowUpload"
                  variant="outlined"
                  prepend-icon="ui-upload-02"
                  :loading="uploadLoading"
                  @click="triggerFileInput"
                >
                  {{ t('filesGallery-modal-upload_button') }}
                </KvButton>
              </div>

              <div class="kv-fgm__filters" v-if="!ai.open && files.length && !typeFilter">
                <KvChip class="kv-fgm__chip" rounded="pill" density="compact" :color="activeType === 'all' ? 'primary' : 'default'" role="button" @click="activeType = 'all'">
                  {{ t('filesGallery-modal-filter_all') }}
                </KvChip>
                <KvChip class="kv-fgm__chip" rounded="pill" density="compact" :color="activeType === 'image' ? 'primary' : 'default'" role="button" @click="activeType = 'image'">
                  {{ t('filesGallery-modal-filter_image') }}
                </KvChip>
                <KvChip class="kv-fgm__chip" rounded="pill" density="compact" :color="activeType === 'pdf' ? 'primary' : 'default'" role="button" @click="activeType = 'pdf'">
                  {{ t('filesGallery-modal-icon_pdf') }}
                </KvChip>
                <KvChip class="kv-fgm__chip" rounded="pill" density="compact" :color="activeType === 'video' ? 'primary' : 'default'" role="button" @click="activeType = 'video'">
                  {{ t('filesGallery-modal-filter_video') }}
                </KvChip>
                <KvChip class="kv-fgm__chip" rounded="pill" density="compact" :color="activeType === 'audio' ? 'primary' : 'default'" role="button" @click="activeType = 'audio'">
                  {{ t('filesGallery-modal-filter_audio') }}
                </KvChip>
                <KvChip class="kv-fgm__chip" rounded="pill" density="compact" :color="activeType === 'other' ? 'primary' : 'default'" role="button" @click="activeType = 'other'">
                  {{ t('filesGallery-modal-filter_other') }}
                </KvChip>
              </div>

              <span v-if="!ai.open && error" class="alert alert-danger mt-3 d-block">{{ error }}</span>
              <span v-if="!ai.open && listLoading && files.length === 0" class="alert alert-info mt-3 d-block">{{ t('filesGallery-modal-loading_files') }}</span>
              <span v-else-if="!ai.open && files.length === 0 && search.trim() === ''" class="alert alert-info mt-3 d-block">
                {{ t('filesGallery-modal-empty_state') }}
              </span>
              <span v-else-if="!ai.open && filteredFiles.length === 0 && search.trim() !== ''" class="alert alert-info mt-3 d-block" v-html="t('filesGallery-no_results_search', [search])"></span>
              <span v-else-if="!ai.open && filteredFiles.length === 0" class="alert alert-info mt-3 d-block">
                {{ t('filesGallery-no_results_filter') }}
              </span>

              <div
                class="kv-fgm__dropzone"
                @dragover.prevent="onPickerDragOver"
                @dragenter.prevent="onPickerDragEnter"
                @dragleave.prevent="onPickerDragLeave"
                @drop.prevent="onPickerDrop"
              >
                <div v-if="isDragActive && allowUpload" class="kv-fgm__drop-overlay" aria-hidden="true">
                  <div class="kv-fgm__drop-overlay-inner">
                    {{ t('filesGallery-modal-drop_here') }}
                  </div>
                </div>

                <!-- Explicit, always-visible upload drop zone (above the grid) -->
                <div v-if="!ai.open && allowUpload" class="files-upload">
                  <div
                    class="upload-area"
                    :class="{ 'is-drag-active': isDragActive }"
                    role="button"
                    tabindex="0"
                    @click="triggerFileInput"
                    @keydown.enter.prevent="triggerFileInput"
                    @keydown.space.prevent="triggerFileInput"
                  >
                    <div class="kv-fgm__upload-title">{{ t('filesGallery-modal-upload_title') }}</div>
                    <div class="kv-fgm__upload-subtitle">
                      {{ t('filesGallery-modal-upload_subtitle', [maxSizeMb]) }}
                    </div>

                    <div v-if="uploadLoading" class="kv-fgm__upload-progress" :aria-label="t('filesGallery-modal-upload_progress')">
                      <div class="kv-fgm__upload-progress-bar" :style="{ width: `${uploadProgress}%` }"></div>
                    </div>
                  </div>
                </div>

                <div v-if="!ai.open && (filteredFiles.length || (allowUpload && $module('ai')))" class="kv-fgm__grid" role="list">
                  <KvButton
                    v-if="allowUpload && $module('ai')"
                    class="kv-fgm__tile kv-fgm__tile--ai"
                    variant="outlined"
                    size="small"
                    :title="t('filesGallery-ai-create_title')"
                    role="listitem"
                    :disabled="uploadLoading || listLoading"
                    @click="openAiCreate"
                  >
                    <div class="kv-fgm__thumb kv-fgm__thumb--ai btn-ai">
                      <span>AI</span>
                    </div>
                  </KvButton>


                  <!-- File tiles -->
                  <KvButton
                  v-for="file in filteredFiles"
                  :key="file.id"
                  class="kv-fgm__tile"
                  :class="{
                    selected: selectedFile && selectedFile.id === file.id,
                    'is-image': isImage(file),
                  }"
                  variant="outlined"
                  size="small"
                  :title="file.name"
                  role="listitem"
                  @click="setSelectedFile(file)"
                  @dblclick="selectable ? selectFile(file) : openFile(file)"
                >
                  <div class="kv-fgm__thumb">
                    <img v-if="isImage(file)" :src="file.url" :alt="file.name" loading="lazy" />
                    <div v-else class="kv-fgm__thumb-icon" aria-hidden="true">{{ fileKindIcon(file) }}</div>
                  </div>
                  <div v-if="!isImage(file)" class="kv-fgm__tile-name" :title="file.name">{{ file.name }}</div>
                  </KvButton>
                </div>

                <!-- KeviAI flow -->
                <div v-if="ai.open" class="kv-fgm__ai">
                  <div class="kv-fgm__ai-header">
                    <div class="kv-fgm__ai-title">
                      {{ ai.mode === "edit" ? t('filesGallery-ai-edit_title') : t('filesGallery-ai-create_title') }}
                    </div>
                    <KvButton variant="text" size="small" @click="closeAi">{{ t('filesGallery-ai-back') }}</KvButton>
                  </div>

                  <span v-if="ai.error" class="alert alert-danger mt-2 d-block">{{ ai.error }}</span>

                  <!-- Step: prompt -->
                  <div v-if="ai.step === 'prompt'" class="kv-fgm__ai-step">
                    <label>{{ ai.mode === "edit" ? t('filesGallery-ai-edit_prompt_label') : t('filesGallery-ai-create_prompt_label') }}</label>
                    <textarea v-model="ai.prompt" class="form-control" rows="5" :placeholder="t('filesGallery-ai-prompt_placeholder')"></textarea>

                    <div class="kv-fgm__ai-actions">
                      <KvButton
                        v-if="ai.mode === 'create'"
                        variant="ai"
                        :loading="ai.loading"
                        :disabled="!ai.prompt.trim() || ai.loading"
                        @click="improveAiPrompt"
                      >
                        {{ t('filesGallery-ai-improve_prompt') }}
                      </KvButton>

                      <KvButton
                        v-else
                        variant="ai"
                        :loading="ai.loading"
                        :disabled="!ai.prompt.trim() || ai.loading"
                        @click="generateAiEdit"
                      >
                        {{ t('filesGallery-ai-generate_edit') }}
                      </KvButton>
                    </div>
                  </div>

                  <!-- Step: improved prompt -->
                  <div v-else-if="ai.step === 'improved'" class="kv-fgm__ai-step">
                    <label>{{ t('filesGallery-ai-improved_prompt_label') }}</label>
                    <textarea v-model="ai.improvedPrompt" class="form-control" rows="6"></textarea>

                    <div class="kv-fgm__ai-actions">
                      <KvButton variant="outlined" :disabled="ai.loading" @click="ai.step = 'prompt'">{{ t('filesGallery-ai-back') }}</KvButton>
                      <KvButton variant="filled" :disabled="!ai.improvedPrompt.trim() || ai.loading" @click="ai.step = 'style'">{{ t('filesGallery-ai-choose_style') }}</KvButton>
                    </div>
                  </div>

                  <!-- Step: style -->
                  <div v-else-if="ai.step === 'style'" class="kv-fgm__ai-step">
                    <label>{{ t('filesGallery-ai-choose_style_label') }}</label>
                    <div class="kv-fgm__ai-style-grid" role="list">
                      <button
                        v-for="style in ai.styles"
                        :key="style.id"
                        type="button"
                        class="kv-fgm__ai-style"
                        :class="{ selected: ai.style === style.id }"
                        role="listitem"
                        @click="ai.style = style.id"
                      >
                        <div class="kv-fgm__ai-style-swatch" :style="{ background: style.swatch }" aria-hidden="true">
                          <img v-if="style.image" :src="style.image" :alt="style.title" loading="lazy" />
                        </div>
                        <div class="kv-fgm__ai-style-title">{{ style.title }}</div>
                        <div class="kv-fgm__ai-style-desc">{{ style.description }}</div>
                      </button>
                    </div>

                    <div class="kv-fgm__ai-actions">
                      <KvButton variant="outlined" :disabled="ai.loading" @click="backFromStyle">{{ t('filesGallery-ai-back') }}</KvButton>
                      <KvButton
                        variant="ai"
                        :loading="ai.loading"
                        :disabled="!ai.style || ai.loading"
                        @click="generateAiImage"
                      >
                        {{ t('filesGallery-ai-generate_image') }}
                      </KvButton>
                    </div>
                  </div>

                  <!-- Step: generating -->
                  <div v-else-if="ai.step === 'generating'" class="kv-fgm__ai-step">
                    <AiLoader :height="12" :duration="30000" />
                    <div class="kv-fgm__ai-generating-text">{{ t('filesGallery-ai-generating') }}</div>
                  </div>

                  <!-- Step: preview -->
                  <div v-else-if="ai.step === 'preview'" class="kv-fgm__ai-step">
                    <div class="kv-fgm__ai-actions">
                      <KvButton variant="outlined" :disabled="ai.saving || ai.loading" @click="ai.mode === 'edit' ? (ai.step = 'prompt') : (ai.step = 'style')">
                        {{ t('filesGallery-ai-adjust') }}
                      </KvButton>
                      <KvButton
                        variant="filled"
                        :loading="ai.saving"
                        :disabled="!ai.previewUrl || ai.saving"
                        @click="saveAiImage"
                      >
                        {{ t('filesGallery-ai-save_to_gallery') }}
                      </KvButton>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <aside class="kv-fgm__preview">
              <div class="kv-fgm__preview-card">
                <div class="kv-fgm__preview-title-top" :title="ai.open ? t('filesGallery-ai-preview') : (selectedFile ? selectedFile.name : '')">
                  {{ ai.open ? t('filesGallery-ai-preview') : (selectedFile ? selectedFile.name : t('filesGallery-ai-select_file')) }}
                </div>
                <div class="kv-fgm__preview-media">
                  <template v-if="ai.open">
                    <template v-if="ai.previewUrl">
                      <img :src="ai.previewUrl" :alt="t('filesGallery-ai-preview')" />
                    </template>
                    <template v-else>
                      <div class="kv-fgm__preview-empty">
                        <div class="kv-fgm__preview-empty-icon" aria-hidden="true">▦</div>
                        <div class="kv-fgm__preview-empty-text">{{ t('filesGallery-ai-preview_generated_here') }}</div>
                      </div>
                    </template>
                  </template>

                  <template v-else-if="selectedFile && isImage(selectedFile)">
                    <img :src="selectedFile.url" :alt="selectedFile.name" />
                  </template>
                  <template v-else-if="selectedFile">
                    <div class="kv-fgm__preview-icon" aria-hidden="true">{{ fileKindIcon(selectedFile) }}</div>
                  </template>
                  <template v-else>
                    <div class="kv-fgm__preview-empty">
                      <div class="kv-fgm__preview-empty-icon" aria-hidden="true">▦</div>
                      <div class="kv-fgm__preview-empty-text">{{ t('filesGallery-ai-select_file') }}</div>
                    </div>
                  </template>
                </div>

                <div class="kv-fgm__preview-info">
                  <div v-if="!ai.open && $module('ai') && selectedFile && isImage(selectedFile)" class="kv-fgm__preview-actions">
                    <KvButton variant="ai" size="small" @click="openAiEdit(selectedFile)">{{ t('filesGallery-ai-edit_with_keviai') }}</KvButton>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </template>

      <template #footer="{ toggle }">
        <div class="d-flex align-items-center w-100 justify-content-end gap-sm">
          <template v-if="ai.open">
            <KvButton variant="outlined" :disabled="ai.loading || ai.saving" @click="closeAi">{{ t('filesGallery-ai-back_to_gallery') }}</KvButton>
            <KvButton
              v-if="selectable && ai.step === 'preview'"
              variant="filled"
              prepend-icon="ui-check"
              :loading="ai.saving"
              :disabled="!ai.previewUrl || ai.saving"
              @click="useAiImage(toggle)"
            >
              {{ t('filesGallery-ai-use_image') }}
            </KvButton>
          </template>
          <template v-else>
            <KvButton variant="outlined" @click="toggle(false)">{{ t('filesGallery-modal-cancel') }}</KvButton>
            <KvButton
              v-if="selectable"
              variant="filled"
              prepend-icon="ui-check"
              :disabled="!selectedFile"
              @click="selectedFile && selectFile(selectedFile)"
            >
              {{ t('filesGallery-modal-use_file') }}
            </KvButton>
          </template>
        </div>
      </template>
    </KvModal>
  </div>
</template>

<script>
import keviApi from "../keviApi";
import KvModal from "@/components/modal/modal.vue";
import KvButton from "@/components/form/kv-button.vue";
import KvChip from "@/components/ui/kv-chip.vue";
import Icon from "@/components/icon/icon.vue";
import AiLoader from "@/components/ui/ai-loader.vue";

export default {
  name: "FilesGalleryModal",
  model: {
    prop: "modelValue",
    event: "update:modelValue",
  },
  components: {
    KvModal,
    KvButton,
    KvChip,
    Icon,
    AiLoader,
  },
  props: {
    id: { type: String, default: "files-gallery-modal" },
    modelValue: { type: Boolean, default: false },
    size: { type: String, default: "large" },
    selectable: { type: Boolean, default: true },
    autoCloseOnSelect: { type: Boolean, default: true },
    allowUpload: { type: Boolean, default: true },
    allowDelete: { type: Boolean, default: false },
    maxSizeMb: { type: Number, default: 20 },
    /**
     * Optional forced type filter. When provided, the modal will apply it and hide the filter pills.
     * Allowed values: all | image | pdf | video | audio | other
     */
    typeFilter: {
      type: String,
      default: null,
      validator(value) {
        return value === null || ["all", "image", "pdf", "video", "audio", "other"].includes(value);
      },
    },
  },
  data() {
    return {
      files: [],
      selectedFile: null,
      listLoading: false,
      uploadLoading: false,
      uploadProgress: 0,
      search: "",
      error: null,
      fileToDelete: null,
      openInFlight: false,
      activeType: "all",
      isDragActive: false,
      dragCounter: 0,
      ai: {
        open: false,
        mode: "create", // create | edit
        step: "prompt", // prompt | improved | style | generating | preview
        prompt: "",
        improvedPrompt: "",
        style: null,
        styles: [
          {
            id: "realistic_photo",
            title: this.$root.t("filesGallery-ai-style-realistic_title"),
            description: this.$root.t("filesGallery-ai-style-realistic_description"),
            swatch: "linear-gradient(135deg, #111827 0%, #6b7280 55%, #e5e7eb 100%)",
            image: "/images/styles/realista.png",
          },
          {
            id: "studio_product",
            title: this.$root.t("filesGallery-ai-style-studio_title"),
            description: this.$root.t("filesGallery-ai-style-studio_description"),
            swatch: "linear-gradient(135deg, #ffffff 0%, #f3f4f6 60%, #d1d5db 100%)",
            image: "/images/styles/produto-estudio.png",
          },
          {
            id: "illustration",
            title: this.$root.t("filesGallery-ai-style-illustration_title"),
            description: this.$root.t("filesGallery-ai-style-illustration_description"),
            swatch: "linear-gradient(135deg, #f97316 0%, #ec4899 45%, #8b5cf6 100%)",
            image: "/images/styles/ilustracao.png",
          },
          {
            id: "watercolor",
            title: this.$root.t("filesGallery-ai-style-watercolor_title"),
            description: this.$root.t("filesGallery-ai-style-watercolor_description"),
            swatch: "linear-gradient(135deg, #93c5fd 0%, #a7f3d0 45%, #fde68a 100%)",
            image: "/images/styles/aquarela.png",
          },
          {
            id: "3d_render",
            title: this.$root.t("filesGallery-ai-style-three_d_title"),
            description: this.$root.t("filesGallery-ai-style-three_d_description"),
            swatch: "linear-gradient(135deg, #0ea5e9 0%, #22c55e 55%, #f59e0b 100%)",
            image: "/images/styles/3d.png",
          },
          {
            id: "minimal",
            title: this.$root.t("filesGallery-ai-style-minimal_title"),
            description: this.$root.t("filesGallery-ai-style-minimal_description"),
            swatch: "linear-gradient(135deg, #111827 0%, #111827 22%, #f9fafb 22%, #f9fafb 100%)",
            image: "/images/styles/minimalista.png",
          },
        ],
        previewUrl: "",
        resultB64: "",
        resultMime: "image/png",
        sourceImageUrl: "",
        loading: false,
        saving: false,
        error: null,
      },
    };
  },
  computed: {
    effectiveType() {
      return this.typeFilter || this.activeType;
    },
    filteredFiles() {
      const bySearch =
        this.search.trim() === ""
          ? this.files
          : this.files.filter((file) =>
              String(file.name || "")
                .toLowerCase()
                .includes(this.search.toLowerCase()),
            );

      const type = this.effectiveType;
      if (type === "all") return bySearch;
      if (type === "image") return bySearch.filter((f) => this.isImage(f));
      if (type === "pdf") return bySearch.filter((f) => String(f.type || "") === "application/pdf");
      if (type === "video") return bySearch.filter((f) => String(f.type || "").startsWith("video/"));
      if (type === "audio") return bySearch.filter((f) => String(f.type || "").startsWith("audio/"));

      // other
      return bySearch.filter((f) => {
        const t = String(f.type || "");
        return !(t.startsWith("image/") || t.startsWith("video/") || t.startsWith("audio/") || t === "application/pdf");
      });
    },
  },
  watch: {
    modelValue(isOpen) {
      if (isOpen) this.onOpen();
    },
    typeFilter: {
      immediate: true,
      handler(newVal) {
        // keep internal state aligned, even if parent changes it while open
        if (newVal) this.activeType = newVal;
      },
    },
  },
  mounted() {
    // no-op
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },

    // Public API (for ref usage)
    open() {
      this.$refs.modal?.open?.();
    },
    close() {
      this.$refs.modal?.close?.();
    },
    toggleShow(forceState) {
      this.$refs.modal?.toggleShow?.(forceState);
    },

    onUpdateModelValue(val) {
      this.$emit("update:modelValue", val);
    },

    handleClose() {
      this.closeAi();
      this.$emit("close");
    },

    async onOpen() {
      if (this.openInFlight) return;
      this.openInFlight = true;

      try {
        if (this.typeFilter) this.activeType = this.typeFilter;
        await this.fetchFiles();
        this.$emit("open");
      } finally {
        this.openInFlight = false;
      }
    },

    async fetchFiles() {
      this.error = null;
      this.listLoading = true;
      try {
        const options = keviApi.getOptions("files-gallery");
        const response = await this.$http(options);

        const payload = response?.data || response?.body || {};
        const rawFiles = payload?.files || {};

        this.files = Object.values(rawFiles);

        // Keep selection stable across refresh (or pick first item if none selected)
        if (this.selectedFile) {
          const updated = this.files.find((f) => f.id === this.selectedFile.id);
          this.selectedFile = updated || null;
        }
        if (!this.selectedFile && this.files.length) {
          this.selectedFile = this.files[0];
        }
      } catch (e) {
        this.error = this.t("filesGallery-notifications-load_error");
      } finally {
        this.listLoading = false;
      }
    },

    onDragOver(event) {
      event.preventDefault();
    },
    onDragEnter(event) {
      event.preventDefault();
      event.target.classList.add("dragover");
    },
    onDragLeave(event) {
      event.target.classList.remove("dragover");
    },
    onDrop(event) {
      event.preventDefault();
      event.target.classList.remove("dragover");
    },

    onPickerDragOver(event) {
      event.preventDefault();
    },
    onPickerDragEnter(event) {
      event.preventDefault();
      if (!this.allowUpload) return;
      this.dragCounter += 1;
      this.isDragActive = true;
    },
    onPickerDragLeave(event) {
      event.preventDefault();
      if (!this.allowUpload) return;
      this.dragCounter = Math.max(0, this.dragCounter - 1);
      if (this.dragCounter === 0) this.isDragActive = false;
    },
    onPickerDrop(event) {
      event.preventDefault();
      if (!this.allowUpload) return;
      this.dragCounter = 0;
      this.isDragActive = false;
      const files = event.dataTransfer?.files;
      this.upload(files);
    },

    triggerFileInput() {
      this.$refs.fileInput?.click?.();
    },
    handleFileUpload(event) {
      const files = event.target.files;
      this.upload(files);
      // allow selecting the same file again
      event.target.value = "";
    },
    handleDrop(event) {
      const files = event.dataTransfer.files;
      this.upload(files);
    },

    upload(files, options = {}) {
      if (!files || !files.length) return;

      const { selectName } = options;

      this.error = null;
      this.uploadLoading = true;
      this.uploadProgress = 0;
      const formData = new FormData();
      const oversizedFiles = [];

      for (let i = 0; i < files.length; i++) {
        if (files[i].size / (1024 * 1024) > this.maxSizeMb) {
          oversizedFiles.push(files[i].name);
        } else {
          formData.append("files[]", files[i]);
        }
      }

      if (oversizedFiles.length > 0) {
        this.error = `${this.t("filesGallery-notifications-oversized_files")} ${oversizedFiles.join(", ")}`;
        this.uploadLoading = false;
        this.uploadProgress = 0;
        return Promise.resolve(false);
      }

      return new Promise((resolve) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", keviApi.getOptions("storage/upload").url, true);
        xhr.setRequestHeader("Authorization", keviApi.getAuthHeader().Authorization);

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            this.uploadProgress = Math.round((event.loaded * 100) / event.total);
          }
        };

        xhr.onload = async () => {
          try {
            if (xhr.status >= 200 && xhr.status < 300) {
              this.error = null;
              this.$store.dispatch("notification/success", this.t("filesGallery-notifications-upload_success"));

              // API returns URLs only; re-fetch to get IDs + metadata
              await this.fetchFiles();

              if (selectName) {
                const found = this.files.find((f) => String(f.name || "") === String(selectName));
                if (found) this.selectedFile = found;
              }

              resolve(true);
            } else {
              const errorResponse = JSON.parse(xhr.responseText || "{}");
              this.error = errorResponse?.data?.message || this.t("filesGallery-notifications-upload_error_message");
              this.$store.dispatch("notification/error", this.t("filesGallery-notifications-upload_error_title"));
              resolve(false);
            }
          } finally {
            this.uploadLoading = false;
            this.uploadProgress = 0;
          }
        };

        xhr.onerror = () => {
          this.error = this.t("filesGallery-notifications-upload_error_message");
          this.uploadLoading = false;
          this.uploadProgress = 0;
          this.$store.dispatch("notification/error", this.t("filesGallery-notifications-upload_error_title"));
          resolve(false);
        };

        xhr.send(formData);
      });
    },

    async copyUrl(url) {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(url);
        } else {
          const textArea = document.createElement("textarea");
          textArea.value = url;
          textArea.style.position = "fixed";
          textArea.style.top = "0";
          textArea.style.left = "-9999px";
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand("copy");
          document.body.removeChild(textArea);
        }

        this.$store.dispatch("notification/success", this.t("filesGallery-notifications-url_copied"));
      } catch (e) {
        this.$store.dispatch("notification/error", this.t("filesGallery-notifications-url_copy_error"));
      }
    },

    openFile(file) {
      if (!file?.url) return;
      window.open(file.url, "_blank", "noopener,noreferrer");
    },

    setSelectedFile(file) {
      this.selectedFile = file;
    },

    isImage(file) {
      return file && file.type && String(file.type).startsWith("image/");
    },

    fileKindIcon(file) {
      const t = String(file?.type || "");
      if (t === "application/pdf") return this.t("filesGallery-modal-icon_pdf");
      if (t.startsWith("video/")) return this.t("filesGallery-modal-icon_video");
      if (t.startsWith("audio/")) return this.t("filesGallery-modal-icon_audio");
      return this.t("filesGallery-modal-icon_file");
    },

    selectFile(file) {
      this.$emit("select", file);
      if (this.autoCloseOnSelect) {
        this.$refs.modal?.close?.();
      }
    },

    openDeleteConfirm(file) {
      this.fileToDelete = file;
      this.$refs.deleteFileConfirm?.open?.();
    },
    async confirmDelete() {
      if (!this.fileToDelete) return;

      try {
        const options = keviApi.getOptions(`storage/delete/${this.fileToDelete.id}`, "DELETE");
        await this.$http(options);

        const index = this.files.findIndex((f) => f.id === this.fileToDelete.id);
        if (index !== -1) this.files.splice(index, 1);
        if (this.selectedFile && this.selectedFile.id === this.fileToDelete.id) {
          this.selectedFile = this.files[0] || null;
        }

        this.$store.dispatch("notification/success", this.t("filesGallery-notifications-delete_success"));
      } catch (error) {
        this.$store.dispatch("notification/error", this.t("filesGallery-notifications-delete_error"));
      } finally {
        this.fileToDelete = null;
      }
    },

    // KeviAI flow
    resetAi() {
      this.ai.open = false;
      this.ai.mode = "create";
      this.ai.step = "prompt";
      this.ai.prompt = "";
      this.ai.improvedPrompt = "";
      this.ai.style = null;
      this.ai.previewUrl = "";
      this.ai.resultB64 = "";
      this.ai.resultMime = "image/png";
      this.ai.sourceImageUrl = "";
      this.ai.loading = false;
      this.ai.saving = false;
      this.ai.error = null;
    },
    openAiCreate() {
      this.resetAi();
      this.ai.open = true;
      this.ai.mode = "create";
      this.ai.step = "prompt";
    },
    openAiEdit(file) {
      if (!file?.url) return;
      this.resetAi();
      this.ai.open = true;
      this.ai.mode = "edit";
      this.ai.step = "prompt";
      this.ai.sourceImageUrl = file.url;
      // seed prompt with something useful
      this.ai.prompt = "";
    },
    closeAi() {
      this.resetAi();
    },
    async improveAiPrompt() {
      if (!this.ai.prompt.trim()) return;
      this.ai.error = null;
      this.ai.loading = true;
      try {
        const options = keviApi.getOptions("kevi-ai/improve-prompt", "POST", { prompt: this.ai.prompt });
        const response = await this.$http(options);
        const payload = response?.data || response?.body || {};
        const improved = payload?.improved_prompt;
        if (!improved) throw new Error(payload?.message || this.t("filesGallery-ai-error_improve_prompt_unavailable"));
        this.ai.improvedPrompt = String(improved).trim();
        this.ai.step = "improved";
      } catch (e) {
        this.ai.error = e?.message || this.t("filesGallery-ai-error_improve_prompt");
      } finally {
        this.ai.loading = false;
      }
    },
    async generateAiImage() {
      const basePrompt = (this.ai.improvedPrompt || this.ai.prompt || "").trim();
      if (!basePrompt) return;
      if (!this.ai.style) return;

      const styleObj = this.ai.styles.find((s) => s.id === this.ai.style);
      const styleText = styleObj ? `${styleObj.title}. ${styleObj.description}` : String(this.ai.style);

      this.ai.error = null;
      this.ai.loading = true;
      this.ai.step = "generating";
      try {
        const options = keviApi.getOptions("kevi-ai/generate-image", "POST", {
          prompt: basePrompt,
          style: styleText,
          size: "1024x1024",
        });
        const response = await this.$http(options);
        const payload = response?.data || response?.body || {};
        const b64 = payload?.b64;
        const mime = payload?.mime || "image/png";
        if (!b64) throw new Error(payload?.message || this.t("filesGallery-ai-error_generate_image_unavailable"));

        this.ai.resultB64 = b64;
        this.ai.resultMime = mime;
        this.ai.previewUrl = `data:${mime};base64,${b64}`;
        this.ai.step = "preview";
      } catch (e) {
        this.ai.error = e?.message || this.t("filesGallery-ai-error_generate_image");
        this.ai.step = "style";
      } finally {
        this.ai.loading = false;
      }
    },
    async generateAiEdit() {
      const prompt = (this.ai.prompt || "").trim();
      if (!prompt || !this.ai.sourceImageUrl) return;

      this.ai.error = null;
      this.ai.loading = true;
      this.ai.step = "generating";
      try {
        const options = keviApi.getOptions("kevi-ai/edit-image", "POST", {
          prompt,
          imageUrl: this.ai.sourceImageUrl,
          size: "1024x1024",
        });
        const response = await this.$http(options);
        const payload = response?.data || response?.body || {};
        const b64 = payload?.b64;
        const mime = payload?.mime || "image/png";
        if (!b64) throw new Error(payload?.message || this.t("filesGallery-ai-error_edit_image_unavailable"));

        this.ai.resultB64 = b64;
        this.ai.resultMime = mime;
        this.ai.previewUrl = `data:${mime};base64,${b64}`;
        this.ai.step = "preview";
      } catch (e) {
        this.ai.error = e?.message || this.t("filesGallery-ai-error_edit_image");
        this.ai.step = "prompt";
      } finally {
        this.ai.loading = false;
      }
    },
    b64ToBlob(b64, mime) {
      const byteChars = atob(String(b64 || ""));
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        byteNumbers[i] = byteChars.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      return new Blob([byteArray], { type: mime || "image/png" });
    },
    async saveAiImage() {
      if (!this.ai.resultB64) return;
      this.ai.error = null;
      this.ai.saving = true;
      try {
        const mime = this.ai.resultMime || "image/png";
        const blob = this.b64ToBlob(this.ai.resultB64, mime);
        const ext = mime === "image/jpeg" ? "jpg" : "png";
        const filename = `keviai-${Date.now()}.${ext}`;
        const file = new File([blob], filename, { type: mime });

        await this.upload([file], { selectName: filename });
        this.$store.dispatch("notification/success", this.t("filesGallery-ai-image_saved"));
      } catch (e) {
        this.ai.error = e?.message || this.t("filesGallery-ai-error_save_image");
      } finally {
        this.ai.saving = false;
      }
    },
    async useAiImage(toggle) {
      try {
        await this.saveAiImage();
        if (this.selectedFile) {
          this.selectFile(this.selectedFile);
          toggle(false);
          this.closeAi();
        }
      } catch (e) {
        // errors already handled in saveAiImage
      }
    },

    backFromStyle() {
      // Go back to prompt editor and keep current text editable there.
      // We collapse "improvedPrompt" into "prompt" so users can tweak and re-improve if desired.
      const current = (this.ai.improvedPrompt || this.ai.prompt || "").trim();
      this.ai.prompt = current;
      this.ai.improvedPrompt = "";
      this.ai.step = "prompt";
    },
  },
};
</script>

<style scoped lang="less">
.kv-fgm-modal {
  /* Force dialog height and prevent the outer body from scrolling */
  :deep(.kv-modal-dialog) {
    height: 80vh;
    max-height: 80vh;
  }

  :deep(.kv-modal-body) {
    overflow: hidden;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
}

.kv-files-gallery-modal {
  /* Mimic Visual Editor settings style */
  height: 100%;
  min-height: 0;

  :deep(label) {
    font-size: 12px;
    font-weight: 600;
    color: #344054;
    margin-bottom: 6px;
  }
  :deep(.form-control) {
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 10px 12px;
    font-size: 14px;
    box-shadow: none;
  }
  :deep(.form-control:focus) {
    border-color: rgba(42, 170, 76, 0.65);
    box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.14);
  }
}

.kv-fgm__layout {
  display: grid;
  /* Invert: preview becomes the larger area, picker gets a tighter column */
  grid-template-columns: 360px minmax(0, 1fr);
  gap: 14px;
  align-items: stretch;
  height: 100%;
  min-height: 0;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
}

.kv-fgm__preview-card {
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  overflow: hidden;
  background: #fff;
  width: 100%;
  height: 100%;
  min-height: 0;
}

.kv-fgm__preview-title-top {
  padding: 12px 12px 10px;
  font-size: 13px;
  font-weight: 600;
  color: #101828;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
}

.kv-fgm__preview {
  min-height: 0;
  height: 100%;
  max-height: 100%;
  overflow-y: auto;
  padding-right: 6px;
  /* keep this simple: fill available space and scroll internally */
  display: block;
}

@media (max-width: 768px) {
  .kv-fgm__layout .kv-fgm__preview {
    display: none !important;
  }
}

.kv-fgm__preview-media {
  /* Reserve space for the preview so the layout doesn't jump,
     but cap it so very large images don't make the panel grow */
  flex: 0 0 auto;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;

  img {
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    display: block;
  }
}

.kv-fgm__preview-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 20px;
  color: #667085;
}
.kv-fgm__preview-empty-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  border: 1px dashed #d0d5dd;
  display: grid;
  place-items: center;
  background: #fff;
  font-size: 18px;
}
.kv-fgm__preview-empty-text {
  font-size: 13px;
  text-align: center;
}

.kv-fgm__preview-icon {
  font-size: 48px;
}

.kv-fgm__preview-info {
  padding: 0;
}

.kv-fgm__preview-actions {
  padding: 10px 12px 12px;
  display: flex;
  justify-content: flex-end;
}

.kv-fgm__picker {
  min-width: 0;
  min-height: 0;
  height: 100%;
  max-height: 100%;
  overflow-y: auto;
  padding-right: 6px;
}

.kv-fgm__toolbar {
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.kv-fgm__filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
}

.kv-fgm__chip {
  cursor: pointer;
  user-select: none;
}

.kv-fgm__chip:hover {
  filter: brightness(0.98);
}

.kv-fgm__search {
  flex: 1;
  min-width: 240px;
  position: relative;
}

.kv-fgm__clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #fff;
  display: grid;
  place-items: center;
  cursor: pointer;
  padding: 0;

  &:hover {
    background: #f9fafb;
  }
}

.kv-fgm__search :deep(.form-control) {
  padding-right: 44px;
  height: 36px;
}

.kv-fgm__dropzone {
  position: relative;
}

.kv-fgm__ai {
  margin-top: 10px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #fff;
  padding: 12px;
}

.kv-fgm__ai-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
}

.kv-fgm__ai-title {
  font-size: 13px;
  font-weight: 500;
  color: #101828;
}

.kv-fgm__ai-step {
  margin-top: 8px;
}

.kv-fgm__ai-actions {
  margin-top: 10px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.kv-fgm__ai-style-grid {
  margin-top: 8px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.kv-fgm__ai-style {
  text-align: left;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 10px;
  background: #fcfcfd;
  cursor: pointer;
  transition: all 0.15s ease;
}

.kv-fgm__ai-style-swatch {
  width: 100%;
  aspect-ratio: 1 / 1;
  min-height: 120px;
  border-radius: 10px;
  border: 1px solid rgba(17, 24, 39, 0.08);
  margin-bottom: 8px;
  overflow: hidden;
  display: block;
  background: #f9fafb;

  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }
}

.kv-fgm__ai-style:hover {
  border-color: rgba(42, 170, 76, 0.55);
  box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.08);
}

.kv-fgm__ai-style.selected {
  border-color: rgba(42, 170, 76, 0.85);
  box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.12);
  background: #fff;
}

.kv-fgm__ai-style-title {
  font-size: 12px;
  font-weight: 700;
  color: #101828;
}

.kv-fgm__ai-style-desc {
  margin-top: 4px;
  font-size: 12px;
  color: #667085;
  line-height: 1.3;
}

.kv-fgm__ai-generating-text {
  margin-top: 10px;
  font-size: 12px;
  color: #667085;
}

.kv-fgm__drop-overlay {
  position: absolute;
  inset: 0;
  z-index: 5;
  border-radius: 16px;
  border: 2px dashed rgba(42, 170, 76, 0.65);
  background: rgba(237, 252, 243, 0.85);
  display: grid;
  place-items: center;
}

.kv-fgm__drop-overlay-inner {
  font-size: 13px;
  font-weight: 600;
  color: #0b9055;
  background: #fff;
  border: 1px solid rgba(42, 170, 76, 0.35);
  border-radius: 12px;
  padding: 10px 12px;
  box-shadow: 0 10px 30px rgba(16, 24, 40, 0.08);
}

.files-upload {
  margin-top: 10px;
  margin-bottom: 12px;

  .upload-area {
    border: 1px dashed #d0d5dd;
    border-radius: 14px;
    padding: 14px;
    background: #fcfcfd;
    cursor: pointer;
    transition: all 0.2s ease;
    outline: none;

    &:hover {
      border-color: rgba(42, 170, 76, 0.55);
      box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.08);
      background: #fff;
    }

    &:focus {
      border-color: rgba(42, 170, 76, 0.65);
      box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.14);
      background: #fff;
    }

    &.is-drag-active {
      border-color: rgba(42, 170, 76, 0.75);
      box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.12);
      background: rgba(237, 252, 243, 0.55);
    }
  }
}

.kv-fgm__upload-title {
  font-weight: 600;
  color: #101828;
  font-size: 13px;
}
.kv-fgm__upload-subtitle {
  margin-top: 4px;
  color: #667085;
  font-size: 12px;
}

.kv-fgm__upload-progress {
  margin-top: 10px;
  width: 100%;
  height: 8px;
  border-radius: 9999px;
  background: #eef2f6;
  overflow: hidden;
}
.kv-fgm__upload-progress-bar {
  height: 100%;
  background: rgba(42, 170, 76, 0.85);
  width: 0%;
  transition: width 0.12s ease;
}

.kv-fgm__grid {
  margin-top: 12px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.kv-fgm__tile {
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #fff;
  padding: 8px;
  text-align: left;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  height: auto;
  white-space: normal;

  &:hover {
    border-color: rgba(42, 170, 76, 0.55);
    box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.08);
  }

  &:focus {
    outline: none;
    border-color: rgba(42, 170, 76, 0.65);
    box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.14);
  }

  &.selected {
    border-color: rgba(42, 170, 76, 0.85);
    box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.12);
  }
}

/* KvButton internal layout -> make it behave like a tile */
.kv-fgm__tile :deep(.kv-button-content) {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}

/* Image tiles: flush thumbnail, no padding/title */
.kv-fgm__tile.is-image {
  padding: 0;
}
.kv-fgm__tile.is-image :deep(.kv-button-content) {
  gap: 0;
}
.kv-fgm__tile.is-image .kv-fgm__thumb {
  border-radius: 14px;
}

.kv-fgm__thumb {
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 12px;
  overflow: hidden;
  background: #f9fafb;
  display: grid;
  place-items: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}
.kv-fgm__thumb-icon {
  font-size: 28px;
}

/* AI tile styling */
.kv-fgm__tile--ai {
  border-color: rgba(42, 170, 76, 0.3);
  grid-column: span 2;
  grid-row: span 2;
  background: linear-gradient(135deg, rgba(237, 252, 243, 0.4) 0%, rgba(255, 255, 255, 0.8) 100%);
}

@media (max-width: 640px) {
  .kv-fgm__tile--ai {
    grid-column: span 2;
    grid-row: span 1;
    aspect-ratio: 16 / 9;
  }
}

.kv-fgm__tile--ai:hover {
  border-color: rgba(42, 170, 76, 0.6);
  box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.12);
  background: linear-gradient(135deg, rgba(237, 252, 243, 0.6) 0%, rgba(255, 255, 255, 1) 100%);
}

.kv-fgm__tile--ai:focus {
  border-color: rgba(42, 170, 76, 0.7);
  box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.16);
}

.kv-fgm__thumb--ai {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 600;
}

.kv-fgm__ai-icon {
  color: rgba(42, 170, 76, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
}

.kv-fgm__tile-name {
  font-size: 12px;
  color: #344054;
  font-weight: 600;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
}
</style>


